import type {RoomPowerLevelsEventContent} from 'matrix-js-sdk/lib/@types/state_events';
import {MEMBERSHIP_POWER_LEVEL_EVENTS} from '@phonehq/rtc-core';

const getBotUsername = (): string => window.__ENV__?.PHONEHQ_AI_USERNAME ?? 'phonehqai';
let currentMatrixUserId: string | undefined;

export const setAiBotSession = (matrixUserId: string | undefined): void => {
    currentMatrixUserId = matrixUserId;
};

export const AI_BOT_POWER_LEVEL = -1;
const MATRIX_DEFAULT_USER_POWER_LEVEL = 0;
const MATRIX_DEFAULT_STATE_EVENT_POWER_LEVEL = 50;
export const AI_BOT_EVENT_POWER_LEVELS = {
    'm.room.message': AI_BOT_POWER_LEVEL,
    'm.room.encrypted': AI_BOT_POWER_LEVEL,
    'm.reaction': AI_BOT_POWER_LEVEL,
    'm.room.redaction': AI_BOT_POWER_LEVEL,
} as const;
export const GROUP_METADATA_EVENT_POWER_LEVELS = {
    'm.room.name': MATRIX_DEFAULT_USER_POWER_LEVEL,
    'm.room.avatar': MATRIX_DEFAULT_USER_POWER_LEVEL,
} as const;

export type MatrixRoomPowerLevels = Readonly<RoomPowerLevelsEventContent>;
export type AiBotRoomStatus = 'checking' | 'pending' | 'inactive' | 'repairRequired' | 'active' | 'failed';

const DIRECT_ROOM_POWER_LEVELS = {
    users_default: MATRIX_DEFAULT_USER_POWER_LEVEL,
    events_default: 0,
    events: {
        ...AI_BOT_EVENT_POWER_LEVELS,
        ...GROUP_METADATA_EVENT_POWER_LEVELS,
        ...MEMBERSHIP_POWER_LEVEL_EVENTS,
    },
    invite: 0,
    kick: 0,
    ban: 50,
    redact: 50,
    state_default: MATRIX_DEFAULT_STATE_EVENT_POWER_LEVEL,
} as const;

export const GROUP_ROOM_POWER_LEVELS = {
    ...DIRECT_ROOM_POWER_LEVELS,
    events: {
        ...DIRECT_ROOM_POWER_LEVELS.events,
        'm.room.topic': 0,
        'm.room.power_levels': 0,
        'm.room.encryption': 0,
        'm.room.history_visibility': 0,
    },
} as const;

interface RoomPowerLevelOptions {
    readonly creatorId: string;
    readonly botUserId: string;
    readonly roomVersion: string;
    readonly invitees?: readonly string[];
}

const creatorHasImplicitPower = (version: string): boolean => {
    const number = Number(version);
    if (!Number.isInteger(number) || number < 1 || number > 12) {
        throw new Error(`Unsupported room version for room permissions: ${version}`);
    }
    return number === 12;
};

export const createGroupPowerLevels = (options: RoomPowerLevelOptions): MatrixRoomPowerLevels => ({
    ...GROUP_ROOM_POWER_LEVELS,
    users: {
        ...(creatorHasImplicitPower(options.roomVersion) ? {} : {[options.creatorId]: 100}),
        [options.botUserId]: AI_BOT_POWER_LEVEL,
    },
});

export const createDirectRoomPowerLevels = (options: RoomPowerLevelOptions): MatrixRoomPowerLevels => ({
    ...DIRECT_ROOM_POWER_LEVELS,
    users: {
        ...(creatorHasImplicitPower(options.roomVersion) ? {} : Object.fromEntries(
            [options.creatorId, ...(options.invitees ?? [])].map(userId => [userId, 100]),
        )),
        [options.botUserId]: AI_BOT_POWER_LEVEL,
    },
});

export const getBotUserIdPrefix = (): string => `@${getBotUsername()}:`;

export const getBotUserId = (matrixUserId: string): string | null => {
    const serverSeparatorIndex = matrixUserId.indexOf(':');
    if (!matrixUserId.startsWith('@') || serverSeparatorIndex <= 1 || serverSeparatorIndex === matrixUserId.length - 1) {
        return null;
    }

    if (!getBotUsername() || /[:@\s]/.test(getBotUsername())) return null;
    return `${getBotUserIdPrefix()}${matrixUserId.slice(serverSeparatorIndex + 1)}`;
};

export const isBotUser = (userId: string, matrixUserId = currentMatrixUserId): boolean =>
    !!matrixUserId && userId === getBotUserId(matrixUserId);

export const isWebhookUser = (userId: string): boolean => {
    const configured = window.__ENV__?.PHONEHQ_WEBHOOK_USER_ID;
    return !!configured && userId === configured;
};

export const isServiceParticipant = (userId: string, matrixUserId = currentMatrixUserId): boolean =>
    isBotUser(userId, matrixUserId) || isWebhookUser(userId);

export const isWebhookMessage = (event: {getSender(): string | undefined; getContent(): Record<string, unknown>}): boolean => {
    const metadata = event.getContent()['app.phonehq.webhook'];
    return isWebhookUser(event.getSender() ?? '') && metadata !== null && typeof metadata === 'object';
};

const getBotDisplayName = (): string => window.__ENV__?.PHONEHQ_AI_DISPLAY_NAME ?? 'PhoneHQ AI';

/** Matrix disambiguates a hero's display name as `Name (@user:server)` when two members share it. */
const DISAMBIGUATION_SUFFIX = /\s*\((@[^)]+)\)$/;

export const stripHeroNameDisambiguation = (name: string): string => name.replace(DISAMBIGUATION_SUFFIX, '');

/**
 * A hero list reaches us as display names only, so the bot has to be
 * recognised by its name. Needed because the room summary the server sends
 * counts PhoneHQ AI as a hero, and the name is calculated from that summary on
 * the initial sliding-sync pass, before the room is in the client store and
 * its members can be read (see `roomNameGenerator`).
 */
export const isBotHeroName = (name: string, matrixUserId = currentMatrixUserId): boolean => {
    const disambiguated = DISAMBIGUATION_SUFFIX.exec(name);
    if (disambiguated) return isBotUser(disambiguated[1], matrixUserId);
    return stripHeroNameDisambiguation(name).trim() === getBotDisplayName().trim();
};

/**
 * The room name for a room that has no `m.room.name` or alias, i.e. one named
 * after who is in it. `memberNames` are the room's joined humans when the room
 * is readable; without it the server's hero summary is used, with PhoneHQ AI
 * removed from both the names and the count it contributes to.
 */
export const buildRoomName = (
    state: {readonly names: readonly string[]; readonly count: number},
    memberNames?: readonly string[],
): string => {
    const heroNames = state.names.filter(name => {
        if (isBotHeroName(name)) return false;
        const userId = DISAMBIGUATION_SUFFIX.exec(name)?.[1];
        return userId ? !isWebhookUser(userId) : name.trim() !== 'PhoneHQ Webhooks';
    }).map(stripHeroNameDisambiguation);
    const botHeroCount = state.names.length - heroNames.length;
    const names = memberNames ?? heroNames;
    const count = memberNames
        ? Math.max(0, memberNames.length - 1)
        : Math.max(0, state.count - 1 - botHeroCount);

    if (!names.length) return 'Empty room';
    if (names.length === 1 && count <= 1) return names[0];
    if (names.length === 2 && count <= 2) return `${names[0]} and ${names[1]}`;
    return count > 1
        ? `${names[0]} and ${count} others`
        : `${names[0]} and 1 other`;
};

/**
 * The room name when nobody but us (and PhoneHQ AI) is joined, e.g. a DM the
 * other person left. Named after who was there, the way Matrix clients do.
 */
export const buildEmptyRoomName = (formerMemberNames: readonly string[]): string =>
    formerMemberNames.length
        ? `Empty room (was ${buildRoomName({names: [], count: 0}, formerMemberNames)})`
        : 'Empty room';

export const hasAiBotInvitePolicy = (
    powerLevels: MatrixRoomPowerLevels,
    botUserId: string,
): boolean => powerLevels.users?.[botUserId] === AI_BOT_POWER_LEVEL
    && Object.keys(AI_BOT_EVENT_POWER_LEVELS).every(eventType =>
        powerLevels.events?.[eventType] === AI_BOT_POWER_LEVEL
    );

export const getAiBotRoomStatus = ({
    roomMembersLoaded,
    botMembership,
    powerLevels,
    botUserId,
}: {
    readonly roomMembersLoaded: boolean;
    readonly botMembership: string | null | undefined;
    readonly powerLevels: MatrixRoomPowerLevels | undefined;
    readonly botUserId: string;
}): AiBotRoomStatus => {
    if (!roomMembersLoaded) return 'checking';
    if (botMembership === 'invite') return 'pending';
    if (botMembership === 'ban') return 'failed';
    if (botMembership !== 'join') return 'inactive';
    if (!powerLevels) return 'checking';

    return hasAiBotInvitePolicy(powerLevels, botUserId) ? 'active' : 'repairRequired';
};

export const canRequestAudioTranscription = (
    audioMessageTimestamp: number,
    botJoinedAt: number | null,
): boolean => {
    return botJoinedAt !== null && botJoinedAt > 0 && audioMessageTimestamp >= botJoinedAt;
};

export interface AiBotMembershipRevision {
    readonly eventId: string;
    readonly timestamp: number;
    readonly membership: string | undefined;
    readonly previousMembership: string | undefined;
    readonly previousEventId: string | undefined;
}

const MAX_MEMBERSHIP_REVISIONS = 64;

export const resolveAiBotJoinTimestamp = async (
    current: AiBotMembershipRevision,
    loadPrevious: (eventId: string) => Promise<AiBotMembershipRevision | undefined>,
): Promise<number | null> => {
    const visited = new Set<string>();
    let revision = current;
    for (let count = 0; count < MAX_MEMBERSHIP_REVISIONS; count++) {
        if (visited.has(revision.eventId) || revision.membership !== 'join'
            || !Number.isFinite(revision.timestamp) || revision.timestamp <= 0) return null;
        visited.add(revision.eventId);
        if (['invite', 'leave', 'ban', 'knock'].includes(revision.previousMembership ?? '')) return revision.timestamp;
        if (revision.previousMembership !== 'join' || !revision.previousEventId) return null;
        const previous = await loadPrevious(revision.previousEventId);
        if (!previous || previous.eventId !== revision.previousEventId) return null;
        revision = previous;
    }
    return null;
};
